import { debApiBase, isApiConfigured } from "./config";
import { ensureAccessToken } from "./auth";
import type { MeetupNight } from "./meetup";

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!isApiConfigured()) {
    throw new ApiError(0, "VITE_DEB_API_URL is not configured");
  }

  const token = await ensureAccessToken();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  let resp: Response;
  try {
    resp = await fetch(`${debApiBase()}${path}`, { ...init, headers });
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Network error";
    throw new ApiError(
      0,
      `${msg} — if this persists, check deb-api CORS or use Vite proxy in dev`,
      err,
    );
  }

  if (resp.status === 401) {
    throw new ApiError(
      401,
      "API rejected your token — staging deb-api may need ZITADEL_DOMAIN=staging-auth.cloudshuttle.com.au",
    );
  }

  const text = await resp.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      if (!resp.ok) {
        const snippet = text.trim().slice(0, 200) || resp.statusText;
        throw new ApiError(
          resp.status,
          resp.status === 404
            ? `API route not found (${path}) — community endpoints may not be deployed to staging yet`
            : `API error ${resp.status}: ${snippet}`,
          text,
        );
      }
      throw new ApiError(resp.status, `Invalid JSON from API: ${text.slice(0, 200)}`);
    }
  }

  if (!resp.ok) {
    const msg =
      (data as { message?: string; error?: string } | null)?.message ??
      (data as { message?: string; error?: string } | null)?.error ??
      resp.statusText;
    throw new ApiError(resp.status, msg || `API error ${resp.status}`, data);
  }

  return data as T;
}

export type MeResponse = {
  data: {
    zitadel_id: string;
    email: string;
    message: string;
  };
};

export type ConnectEvent = {
  ticketing_event_id: string;
  name: string;
  city?: string;
  event_date?: string;
  opens_at?: string;
  closes_at?: string;
  connect_enabled: boolean;
  /** False when the city's claim/scan window is closed or not yet open. */
  connect_open?: boolean;
};

export type EventsResponse = {
  data: ConnectEvent[];
};

export function fetchHealthz(): Promise<{ status: string }> {
  return request("/healthz");
}

export function fetchMe(): Promise<MeResponse> {
  return request("/api/v1/protected/me");
}

export function fetchConnectEvents(): Promise<EventsResponse> {
  return request("/api/v1/protected/community/events");
}

export type { MeetupNight };

export type MeetupNightsResponse = {
  data: MeetupNight[];
};

export type MeetupNightResponse = {
  data: MeetupNight;
};

export type MeetupRegistration = {
  id: string;
  slug: string;
  status: string;
  display_name: string;
  short_code?: string;
  qr_token?: string;
  ics?: string;
  discord_invite_url?: string;
  event?: {
    slug: string;
    title: string;
    city: string;
    starts_at: string;
    location?: string | null;
  };
};

export type MeetupRegistrationResponse = {
  data: MeetupRegistration;
};

export type MeetupHistorySpeaker = {
  name: string;
  talk?: string;
  role?: string;
};

export type MeetupHistoryNight = {
  title: string;
  city: string;
  starts_at?: string;
  venue?: string;
  speakers?: MeetupHistorySpeaker[];
};

export type MeetupMe = {
  user_id: string;
  email: string;
  name: string;
  home_city: string;
  history?: MeetupHistoryNight[];
};

export function fetchMeetupNights(): Promise<MeetupNightsResponse> {
  return request("/api/v1/protected/meetup/events");
}

export function fetchMeetupNight(slug: string): Promise<MeetupNightResponse> {
  return request(`/api/v1/protected/meetup/events/${encodeURIComponent(slug)}`);
}

export function fetchMeetupMine(slug: string): Promise<MeetupRegistrationResponse> {
  return request(`/api/v1/protected/meetup/events/${encodeURIComponent(slug)}/register`);
}

export function registerMeetup(slug: string): Promise<MeetupRegistrationResponse> {
  return request(`/api/v1/protected/meetup/events/${encodeURIComponent(slug)}/register`, {
    method: "POST",
  });
}

export function cancelMeetup(slug: string): Promise<void> {
  return request(`/api/v1/protected/meetup/events/${encodeURIComponent(slug)}/register`, {
    method: "DELETE",
  });
}

export function fetchMeetupMe(): Promise<{ data: MeetupMe }> {
  return request("/api/v1/protected/meetup/me");
}

export function updateMeetupHomeCity(homeCity: string): Promise<{ data: MeetupMe }> {
  return request("/api/v1/protected/meetup/me", {
    method: "PUT",
    body: JSON.stringify({ home_city: homeCity }),
  });
}

export type CommunityProfile = {
  id?: string;
  user_id?: string;
  ticketing_event_id: string;
  attendee_id?: string | null;
  display_name?: string | null;
  headline?: string | null;
  bio?: string | null;
  company?: string | null;
  job_role?: string | null;
  avatar_url?: string | null;
  looking_for?: string | null;
  networking_opt_in: boolean;
  show_email_to_connections?: boolean;
  visibility: "hidden" | "directory";
  open_to_hiring?: boolean;
  open_to_job_search?: boolean;
  open_to_mentoring?: boolean;
  open_to_speaking?: boolean;
  interest_tag_ids: string[];
};

export type ProfileResponse = {
  data: CommunityProfile | null;
  ever_claimed_ticket?: boolean;
  connect_pass?: boolean;
  connect_pass_expires?: string;
  member_kind?: string;
};

export type InterestTag = {
  id: string;
  slug: string;
  label: string;
  sort_order: number;
};

export type InterestsResponse = {
  data: InterestTag[];
};

export type ClaimSupportCTA = {
  email: string;
  title: string;
  body: string;
};

export type ClaimErrorBody = {
  error: string;
  message?: string;
  support?: ClaimSupportCTA;
};

export function claimTicket(ticketingEventId: string): Promise<ProfileResponse> {
  return request("/api/v1/protected/community/claim-ticket", {
    method: "POST",
    body: JSON.stringify({ ticketing_event_id: ticketingEventId }),
  });
}

export type SeatFields = {
  first_name: string;
  last_name: string;
  email: string;
  company?: string | null;
  job_role?: string | null;
  dietary_requirements?: string | null;
  attending_after_party?: boolean | null;
  share_contact_with_sponsors?: boolean | null;
};

export type NameSlotView = {
  token: string;
  attendee_id: string;
  slot_index: number;
  assigned: boolean;
  event_id: string;
  event_name: string;
  city: string;
  event_date_label?: string;
  order_status: string;
  refunded: boolean;
  fields: SeatFields;
  assign_url: string;
};

export type BuyerOrderSeat = {
  attendee_id: string;
  slot_index: number;
  assigned: boolean;
  assign_url?: string;
  barcode?: string;
  fields: SeatFields;
  assigned_at?: string | null;
  assignment_source?: string | null;
};

export type BuyerOrder = {
  order_id: string;
  event_id: string;
  event_name: string;
  city: string;
  event_date_label?: string;
  status: string;
  quantity: number;
  total_cents: number;
  currency: string;
  ticket_type_name?: string;
  manage_url: string;
  seats: BuyerOrderSeat[];
};

/** Public name-slot GET (no Zitadel; token is the credential). */
export async function fetchNameSlot(token: string): Promise<{ data: NameSlotView }> {
  if (!isApiConfigured()) {
    throw new ApiError(0, "VITE_DEB_API_URL is not configured");
  }
  const resp = await fetch(`${debApiBase()}/api/v1/public/name-slots/${encodeURIComponent(token)}`, {
    headers: { Accept: "application/json" },
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw new ApiError(
      resp.status,
      (data as { message?: string } | null)?.message ?? resp.statusText,
      data,
    );
  }
  return data as { data: NameSlotView };
}

export async function submitNameSlot(
  token: string,
  fields: SeatFields,
): Promise<{ data: NameSlotView }> {
  if (!isApiConfigured()) {
    throw new ApiError(0, "VITE_DEB_API_URL is not configured");
  }
  const resp = await fetch(`${debApiBase()}/api/v1/public/name-slots/${encodeURIComponent(token)}`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(fields),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw new ApiError(
      resp.status,
      (data as { message?: string } | null)?.message ?? resp.statusText,
      data,
    );
  }
  return data as { data: NameSlotView };
}

export function fetchMyOrders(): Promise<{ data: BuyerOrder[] }> {
  return request("/api/v1/protected/community/my-orders");
}

export function fetchMyOrder(orderId: string): Promise<{ data: BuyerOrder }> {
  return request(`/api/v1/protected/community/my-orders/${encodeURIComponent(orderId)}`);
}

export function updateMyOrderSeat(
  orderId: string,
  attendeeId: string,
  fields: SeatFields,
): Promise<{ data: NameSlotView }> {
  return request(
    `/api/v1/protected/community/my-orders/${encodeURIComponent(orderId)}/seats/${encodeURIComponent(attendeeId)}`,
    { method: "PUT", body: JSON.stringify(fields) },
  );
}

export function resendMyOrderSeat(
  orderId: string,
  attendeeId: string,
  toEmail?: string,
): Promise<{ data: { assign_url: string; to_email: string } }> {
  return request(
    `/api/v1/protected/community/my-orders/${encodeURIComponent(orderId)}/seats/${encodeURIComponent(attendeeId)}/resend`,
    {
      method: "POST",
      body: JSON.stringify(toEmail ? { to_email: toEmail } : {}),
    },
  );
}

export function fetchProfile(ticketingEventId?: string): Promise<ProfileResponse> {
  if (ticketingEventId) {
    const q = new URLSearchParams({ ticketing_event_id: ticketingEventId });
    return request(`/api/v1/protected/community/me/profile?${q}`);
  }
  return request("/api/v1/protected/community/me/profile");
}

export type UpdateProfilePayload = {
  ticketing_event_id: string;
  display_name?: string;
  headline?: string;
  bio?: string;
  company?: string;
  job_role?: string;
  looking_for?: string;
  networking_opt_in?: boolean;
  show_email_to_connections?: boolean;
  visibility?: "hidden" | "directory";
  interest_tag_ids?: string[];
};

export function updateProfile(payload: UpdateProfilePayload): Promise<ProfileResponse> {
  return request("/api/v1/protected/community/me/profile", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export type MemberProfile = {
  id?: string;
  user_id?: string;
  display_name?: string | null;
  headline?: string | null;
  bio?: string | null;
  company?: string | null;
  job_role?: string | null;
  avatar_url?: string | null;
  looking_for?: string | null;
  show_email_to_connections?: boolean;
  open_to_hiring?: boolean;
  open_to_job_search?: boolean;
  open_to_mentoring?: boolean;
  open_to_speaking?: boolean;
  member_kind?: string;
  interest_tag_ids: string[];
  skill_tag_ids?: string[];
};

export type MemberProfileResponse = {
  data: MemberProfile | null;
  ever_claimed_ticket?: boolean;
  connect_pass?: boolean;
  connect_pass_expires?: string;
  member_kind?: string;
};

export function fetchMemberProfile(): Promise<MemberProfileResponse> {
  return request("/api/v1/protected/community/me/member-profile");
}

export type UpdateMemberProfilePayload = {
  display_name?: string;
  headline?: string;
  bio?: string;
  company?: string;
  job_role?: string;
  looking_for?: string;
  show_email_to_connections?: boolean;
  open_to_hiring?: boolean;
  open_to_job_search?: boolean;
  open_to_mentoring?: boolean;
  open_to_speaking?: boolean;
  interest_tag_ids?: string[];
  skill_tag_ids?: string[];
};

export function updateMemberProfile(payload: UpdateMemberProfilePayload): Promise<MemberProfileResponse> {
  return request("/api/v1/protected/community/me/member-profile", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function hasMemberProfileContent(profile: MemberProfile | null | undefined): boolean {
  return !!profile?.display_name?.trim();
}

export function fetchInterests(): Promise<InterestsResponse> {
  return request("/api/v1/protected/community/interests");
}

export type SkillTag = {
  id: string;
  slug: string;
  label: string;
  category: string;
  sort_order: number;
};

export type SkillsResponse = {
  data: SkillTag[];
};

export function fetchSkills(): Promise<SkillsResponse> {
  return request("/api/v1/protected/community/skills");
}

export type SkillSummary = {
  id: string;
  slug: string;
  label: string;
  category: string;
};

export type ProjectCard = {
  id: string;
  title: string;
  summary?: string;
  cover_image_url?: string;
  is_pinned: boolean;
  skills?: SkillSummary[];
};

export type Project = ProjectCard & {
  member_profile_id?: string;
  user_id?: string;
  description?: string;
  repo_url?: string;
  demo_url?: string;
  article_url?: string;
  visibility: "hidden" | "directory";
  skill_tag_ids?: string[];
  created_at?: string;
  updated_at?: string;
};

export type ProjectsResponse = {
  data: Project[];
};

export type ProjectResponse = {
  data: Project;
};

export type UpsertProjectPayload = {
  title?: string;
  summary?: string;
  description?: string;
  cover_image_url?: string;
  repo_url?: string;
  demo_url?: string;
  article_url?: string;
  is_pinned?: boolean;
  visibility?: "hidden" | "directory";
  skill_tag_ids?: string[];
};

export function fetchMyProjects(): Promise<ProjectsResponse> {
  return request("/api/v1/protected/community/me/projects");
}

export function fetchProject(projectId: string): Promise<ProjectResponse> {
  return request(`/api/v1/protected/community/projects/${encodeURIComponent(projectId)}`);
}

export function createProject(payload: UpsertProjectPayload): Promise<ProjectResponse> {
  return request("/api/v1/protected/community/me/projects", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateProject(projectId: string, payload: UpsertProjectPayload): Promise<ProjectResponse> {
  return request(`/api/v1/protected/community/me/projects/${encodeURIComponent(projectId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteProject(projectId: string): Promise<{ ok: boolean }> {
  return request(`/api/v1/protected/community/me/projects/${encodeURIComponent(projectId)}`, {
    method: "DELETE",
  });
}

export function reportProject(projectId: string, reason: string): Promise<{ ok: boolean }> {
  return request("/api/v1/protected/community/project-reports", {
    method: "POST",
    body: JSON.stringify({ project_id: projectId, reason }),
  });
}

export function hasClaimedTicket(profile: CommunityProfile | null | undefined): boolean {
  return !!profile?.attendee_id;
}

/** Cross-event ticket claim: true if the user has claimed a ticket to any DEB event. */
export function everClaimedTicket(
  source: { ever_claimed_ticket?: boolean } | null | undefined,
): boolean {
  return !!source?.ever_claimed_ticket;
}

/** Saved profile fields without a linked ticket (not visible in directory). */
export function hasDraftProfile(profile: CommunityProfile | null | undefined): boolean {
  return !!profile?.display_name?.trim() && !profile.attendee_id;
}

export function isProfileOnboardingComplete(profile: CommunityProfile | null | undefined): boolean {
  if (!profile?.attendee_id) return false;
  if (!profile.networking_opt_in) return false;
  if (profile.visibility !== "directory") return false;
  if (!profile.display_name?.trim()) return false;
  return true;
}

export function isClaimRequiredError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  const code = (err.body as { error?: string } | null)?.error;
  return code === "claim_required";
}

export function parseClaimError(err: unknown): ClaimErrorBody | null {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== "object") return null;
  const body = err.body as ClaimErrorBody;
  if (body.error === "ticket_not_matched") return body;
  return null;
}

export type DirectoryInterest = {
  id: string;
  slug: string;
  label: string;
};

export type DirectoryCard = {
  id: string;
  display_name?: string;
  headline?: string;
  company?: string;
  job_role?: string;
  avatar_url?: string;
  interests: DirectoryInterest[];
  skills?: SkillSummary[];
  pinned_project?: ProjectCard;
  open_to_hiring?: boolean;
  open_to_job_search?: boolean;
  open_to_mentoring?: boolean;
  open_to_speaking?: boolean;
};

export type DirectoryResponse = {
  data: DirectoryCard[];
  next_cursor?: string;
};

export type DirectoryQuery = {
  eventId?: string;
  q?: string;
  company?: string;
  job_role?: string;
  interests?: string[];
  open_to?: string[];
  cursor?: string;
  limit?: number;
};

function buildDirectoryQuery(params: DirectoryQuery): URLSearchParams {
  const q = new URLSearchParams();
  if (params.eventId) q.set("ticketing_event_id", params.eventId);
  if (params.q?.trim()) q.set("q", params.q.trim());
  if (params.company?.trim()) q.set("company", params.company.trim());
  if (params.job_role?.trim()) q.set("job_role", params.job_role.trim());
  if (params.cursor) q.set("cursor", params.cursor);
  if (params.limit) q.set("limit", String(params.limit));
  for (const id of params.interests ?? []) {
    q.append("interest", id);
  }
  for (const value of params.open_to ?? []) {
    q.append("open_to", value);
  }
  return q;
}

export function fetchDirectory(params: DirectoryQuery): Promise<DirectoryResponse> {
  return request(`/api/v1/protected/community/directory?${buildDirectoryQuery(params)}`);
}

export type ProfileDetail = CommunityProfile & {
  interests?: DirectoryInterest[];
  skills?: SkillSummary[];
  projects?: ProjectCard[];
};

export type ProfileDetailResponse = {
  data: ProfileDetail;
};

export function fetchProfileDetail(userId: string, eventId?: string): Promise<ProfileDetailResponse> {
  const q = new URLSearchParams();
  if (eventId) q.set("ticketing_event_id", eventId);
  const qs = q.toString();
  return request(`/api/v1/protected/community/profiles/${userId}${qs ? `?${qs}` : ""}`);
}

export async function uploadAvatar(file: File, ticketingEventId?: string): Promise<MemberProfileResponse> {
  if (!isApiConfigured()) {
    throw new ApiError(0, "VITE_DEB_API_URL is not configured");
  }
  const token = await ensureAccessToken();
  const form = new FormData();
  if (ticketingEventId) form.append("ticketing_event_id", ticketingEventId);
  form.append("avatar", file);

  const headers = new Headers({ Accept: "application/json" });
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const resp = await fetch(`${debApiBase()}/api/v1/protected/community/me/avatar`, {
    method: "POST",
    headers,
    body: form,
  });

  const text = await resp.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new ApiError(resp.status, text.slice(0, 200));
    }
  }
  if (!resp.ok) {
    const msg =
      (data as { message?: string; error?: string } | null)?.message ??
      (data as { message?: string; error?: string } | null)?.error ??
      resp.statusText;
    throw new ApiError(resp.status, msg || `Upload failed (${resp.status})`, data);
  }
  return data as MemberProfileResponse;
}

export type ConnectionStatus = "pending" | "accepted" | "declined" | "cancelled";

export type ConnectionPeer = {
  user_id: string;
  display_name?: string;
  headline?: string;
  company?: string;
  avatar_url?: string;
  email?: string;
};

export type ConnectionItem = {
  id: string;
  from_user_id: string;
  to_user_id: string;
  status: ConnectionStatus;
  direction: "incoming" | "outgoing";
  request_note?: string;
  created_at: string;
  responded_at?: string;
  peer: ConnectionPeer;
};

export type ConnectionsResponse = {
  data: ConnectionItem[];
};

export function fetchConnections(
  eventId?: string,
  status?: ConnectionStatus,
): Promise<ConnectionsResponse> {
  const q = new URLSearchParams();
  if (eventId) q.set("ticketing_event_id", eventId);
  if (status) q.set("status", status);
  const qs = q.toString();
  return request(`/api/v1/protected/community/connections${qs ? `?${qs}` : ""}`);
}

export function createConnection(
  toUserId: string,
  eventId?: string,
  requestNote?: string,
): Promise<{ data: ConnectionItem }> {
  return request("/api/v1/protected/community/connections", {
    method: "POST",
    body: JSON.stringify({
      to_user_id: toUserId,
      ticketing_event_id: eventId || undefined,
      request_note: requestNote?.trim() || undefined,
    }),
  });
}

export function respondConnection(id: string, action: "accept" | "decline"): Promise<{ data: ConnectionItem }> {
  return request(`/api/v1/protected/community/connections/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ action }),
  });
}

export function cancelConnection(id: string): Promise<{ data: ConnectionItem }> {
  return request(`/api/v1/protected/community/connections/${id}`, { method: "DELETE" });
}

export function reportProfile(
  userId: string,
  eventId: string | undefined,
  reason: string,
): Promise<{ message: string }> {
  return request("/api/v1/protected/community/reports", {
    method: "POST",
    body: JSON.stringify({
      user_id: userId,
      ticketing_event_id: eventId || undefined,
      reason,
    }),
  });
}

export function connectionWithPeer(
  connections: ConnectionItem[] | undefined,
  userId: string,
): ConnectionItem | undefined {
  return connections?.find(
    (c) => c.peer.user_id === userId || c.from_user_id === userId || c.to_user_id === userId,
  );
}

export type ScanQuota = {
  is_sponsor: boolean;
  sponsor_org?: string;
  scans_used?: number;
  scans_limit?: number;
  scans_remaining?: number;
};

export type SponsorLead = {
  note: string | null;
  rating: number | null;
  follow_up: FollowUpAction | null;
};

export type BadgeScanResult = {
  scan_id: string;
  attendee_id: string;
  user_id?: string;
  profile_id?: string;
  can_manage_lead?: boolean;
  sponsor_lead?: SponsorLead;
  display: {
    display_name?: string;
    headline?: string;
    company?: string;
    job_role?: string;
    avatar_url?: string;
    bio?: string;
    looking_for?: string;
    interests?: { id: string; slug: string; label: string }[];
  };
  contact: {
    email?: string;
    ticket_email_redacted?: string;
  };
  connection?: { id: string; status: string; created: boolean };
  flags: {
    in_directory: boolean;
    share_contact_with_sponsors: boolean;
    ask_to_join_connect: boolean;
  };
  quota: ScanQuota;
};

export function fetchScanQuota(eventId: string): Promise<{ data: ScanQuota }> {
  const q = new URLSearchParams({ ticketing_event_id: eventId });
  return request(`/api/v1/protected/community/scan/quota?${q}`);
}

export function scanBadge(eventId: string, ticketId: string): Promise<{ data: BadgeScanResult }> {
  return request("/api/v1/protected/community/scan", {
    method: "POST",
    body: JSON.stringify({ ticketing_event_id: eventId, ticket_id: ticketId }),
  });
}

export type FollowUpAction = "email" | "demo" | "materials" | "call" | "linkedin" | "pass";

export type SponsorLeadScope = "mine" | "team";

export type SponsorLeadListItem = {
  scan_id: string;
  attendee_id: string;
  tt_ticket_id: string;
  scanned_at: string;
  can_edit: boolean;
  user_id?: string;
  scanner_user_id: string;
  scanner_name?: string;
  scanner_email?: string;
  sponsor_lead: SponsorLead;
  display: {
    display_name?: string;
    headline?: string;
    company?: string;
    job_role?: string;
    avatar_url?: string;
  };
  contact: {
    email?: string;
    ticket_email_redacted?: string;
  };
};

export type SponsorLeadList = {
  scope: SponsorLeadScope;
  sponsor_org?: string;
  items: SponsorLeadListItem[];
};

export function fetchSponsorLeads(
  eventId: string,
  scope: SponsorLeadScope = "mine",
): Promise<{ data: SponsorLeadList }> {
  const q = new URLSearchParams({ ticketing_event_id: eventId, scope });
  return request(`/api/v1/protected/community/scan/leads?${q}`);
}

export function updateSponsorLead(
  scanId: string,
  lead: { note: string; rating: number | null; follow_up: FollowUpAction | null },
): Promise<{ data: { scan_id: string; sponsor_lead: SponsorLead } }> {
  return request(`/api/v1/protected/community/scan/${scanId}/lead`, {
    method: "PATCH",
    body: JSON.stringify({
      note: lead.note,
      rating: lead.rating,
      follow_up: lead.follow_up,
    }),
  });
}

export type OpportunityType = "job" | "gig" | "mentorship" | "collab" | "speaking";
export type OpportunityStatus = "open" | "filled" | "closed";

export type Opportunity = {
  id: string;
  posted_by_user_id: string;
  type: OpportunityType;
  title: string;
  description: string;
  location?: string;
  remote_ok: boolean;
  apply_url?: string;
  sponsor_org?: string;
  status: OpportunityStatus;
  expires_at?: string;
  skills?: SkillSummary[];
  created_at?: string;
  updated_at?: string;
};

export type OpportunitiesResponse = {
  data: Opportunity[];
  next_cursor?: string;
};

export type OpportunityResponse = {
  data: Opportunity;
};

export type OpportunityQuery = {
  type?: OpportunityType;
  skills?: string[];
  remote_only?: boolean;
  cursor?: string;
  limit?: number;
};

export type CreateOpportunityPayload = {
  type: OpportunityType;
  title: string;
  description: string;
  location?: string;
  remote_ok?: boolean;
  apply_url?: string;
  expires_at?: string;
  skill_tag_ids?: string[];
};

export function fetchOpportunities(params: OpportunityQuery = {}): Promise<OpportunitiesResponse> {
  const q = new URLSearchParams();
  if (params.type) q.set("type", params.type);
  if (params.remote_only) q.set("remote_only", "true");
  if (params.cursor) q.set("cursor", params.cursor);
  if (params.limit) q.set("limit", String(params.limit));
  for (const id of params.skills ?? []) {
    q.append("skill", id);
  }
  const qs = q.toString();
  return request(`/api/v1/protected/community/opportunities${qs ? `?${qs}` : ""}`);
}

export function fetchOpportunity(id: string): Promise<OpportunityResponse> {
  return request(`/api/v1/protected/community/opportunities/${encodeURIComponent(id)}`);
}

export function createOpportunity(payload: CreateOpportunityPayload): Promise<OpportunityResponse> {
  return request("/api/v1/protected/community/opportunities", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateOpportunityStatus(
  id: string,
  status: OpportunityStatus,
): Promise<OpportunityResponse> {
  return request(`/api/v1/protected/community/opportunities/${encodeURIComponent(id)}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export function expressOpportunityInterest(id: string): Promise<{ data: { id: string } }> {
  return request(`/api/v1/protected/community/opportunities/${encodeURIComponent(id)}/interest`, {
    method: "POST",
    body: "{}",
  });
}

export function reportOpportunity(opportunityId: string, reason: string): Promise<{ ok: boolean }> {
  return request("/api/v1/protected/community/opportunity-reports", {
    method: "POST",
    body: JSON.stringify({ opportunity_id: opportunityId, reason }),
  });
}